import { useState, useEffect, useRef, forwardRef } from 'react';
import './BlocklyComponent.css';

import * as Blockly from 'blockly/core';
import * as localeEn from 'blockly/msg/en';
import 'blockly/blocks';
import './workspace';

import { VmtTypesCheckPluginInfo } from './VMTConnectionChecker';

import { useAppContext } from "app-context";
import { Themes } from 'utils/const';
import { useCombinedRefs } from 'utils/misc';

import { VmtFlyout } from 'blockly_vmt/vavanya-generator/src/vmt_blocks/building_blocks/fields/VmtFlyout/VmtFlyout';

import './VMTRenderer';

Blockly.setLocale(localeEn);

// const originalContextMenuFn = Blockly.ContextMenu.show;
// Blockly.ContextMenu.show = (e, menuOptions, rtl) => {
//     console.log('Blockly.ContextMenu.show');
//     originalContextMenuFn(e, menuOptions, rtl);
// };

/*
// Attach event listeners.
Blockly.bindEventWithChecks_(zoomresetSvg, 'mousedown', null, function(e) {
  ws.markFocused();
  ws.setScale(ws.options.zoomOptions.startScale);
  ws.beginCanvasTransition();
  ws.scrollCenter();
  setTimeout(ws.endCanvasTransition.bind(ws), 500);
  Blockly.Touch.clearTouchIdentifier();  // Don't block future drags.
  e.stopPropagation();  // Don't start a workspace scroll.
  e.preventDefault();  // Stop double-clicking from selecting text.
});
 
*/

// !!! MONKEY PATCH
// Change the default healing stack behavior during the block dragging
/**
 * Start a gesture: update the workspace to indicate that a gesture is in
 * progress and bind mousemove and mouseup handlers.
 * @param {!Event} e A mouse down or touch start event.
 * @package
 */
Blockly.Gesture.prototype.doStart = function (e) {
    if (!this.startWorkspace_) {
        throw new Error(
            'Cannot start the touch gesture becauase the start ' +
            'workspace is undefined',
        );
    }
    this.isPinchZoomEnabled =
        this.startWorkspace_.options.zoomOptions &&
        this.startWorkspace_.options.zoomOptions.pinch;

    if (Blockly.browserEvents.isTargetInput(e)) {
        this.cancel();
        return;
    }

    this.gestureHasStarted = true;

    Blockly.blockAnimations.disconnectUiStop();

    this.startWorkspace_.updateScreenCalculationsIfScrolled();
    if (this.startWorkspace_.isMutator) {
        // Mutator's coordinate system could be out of date because the bubble was
        // dragged, the block was moved, the parent workspace zoomed, etc.
        this.startWorkspace_.resize();
    }

    // Keep track of which field owns the dropdown before we close it.
    this.currentDropdownOwner = Blockly.DropDownDiv.getOwner();

    // Hide chaff also hides the flyout, so don't do it if the click is in a
    // flyout.
    this.startWorkspace_.hideChaff(!!this.flyout);

    this.startWorkspace_.markFocused();
    this.mostRecentEvent = e;

    Blockly.Tooltip.block();

    if (this.targetBlock) {
        this.targetBlock.select();
    }

    if (Blockly.browserEvents.isRightButton(e)) {
        this.handleRightClick(e);
        return;
    }

    if (e.type.toLowerCase() === 'pointerdown' && e.pointerType !== 'mouse') {
        Touch.longStart(e, this);
    }

    this.mouseDownXY = new Blockly.utils.Coordinate(e.clientX, e.clientY);
    // this.healStack = e.altKey || e.ctrlKey || e.metaKey;
    this.healStack = !(e.altKey || e.ctrlKey || e.metaKey);

    this.bindMouseEvents(e);

    if (!this.isEnding_) {
        this.handleTouchStart(e);
    }
};


const vmtDarkTheme = Blockly.Theme.defineTheme('vmtThemeDark', {
    'base': Blockly.Themes.Classic,
    // 'blockStyles': blockStyles,
    // 'categoryStyles': categoryStyles,
    // 'componentStyles': componentStyles,
    'componentStyles': {
        'workspaceBackgroundColour': '#1e1e1e',
        'toolboxBackgroundColour': 'blackBackground',
        'toolboxForegroundColour': '#fff',
        'flyoutBackgroundColour': '#252526',
        'flyoutForegroundColour': '#ccc',
        'flyoutOpacity': 1,
        'scrollbarColour': '#797979',
        'insertionMarkerColour': '#fff',
        'insertionMarkerOpacity': 0.3,
        'scrollbarOpacity': 0.4,
        'cursorColour': '#d0d0d0',
        'blackBackground': '#333',
    },
    'fontStyle': {
        "family": 'SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace',
        "weight": "lighter",
        "size": 11
    },
    // 'startHats': true
});

const vmtLightTheme = Blockly.Theme.defineTheme('vmtThemeLight', {
    'base': Blockly.Themes.Classic,
    // 'blockStyles': blockStyles,
    // 'categoryStyles': categoryStyles,
    // 'componentStyles': componentStyles,
    // 'componentStyles': {
    //     'workspaceBackgroundColour': '#1e1e1e',
    //     'toolboxBackgroundColour': 'blackBackground',
    //     'toolboxForegroundColour': '#fff',
    //     'flyoutBackgroundColour': '#252526',
    //     'flyoutForegroundColour': '#ccc',
    //     'flyoutOpacity': 1,
    //     'scrollbarColour': '#797979',
    //     'insertionMarkerColour': '#fff',
    //     'insertionMarkerOpacity': 0.3,
    //     'scrollbarOpacity': 0.4,
    //     'cursorColour': '#d0d0d0',
    //     'blackBackground': '#333',
    // },
    'fontStyle': {
        "family": 'SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace',
        "weight": "lighter",
        "size": 11
    },
    // 'startHats': true
});

/**
 * Register the VmtFlyout for the workspace.
 * @param {Blockly.Workspace} workspace The workspace to register the flyout for.
 */
const registerVmtFlyout = (workspace) => {
    const vmtFlyout = new VmtFlyout(
        new Blockly.Options({
            scrollbars: false,
            rtl: workspace.RTL,
            renderer: workspace.options.renderer,
            // renderer: 'vmt_renderer',
            rendererOverrides: workspace.options.rendererOverrides,
            parentWorkspace: workspace,
        })
    );
    workspace.vmtFlyout_ = vmtFlyout;
    Blockly.utils.dom.insertAfter(vmtFlyout.createDom('g'),
        workspace.svgBubbleCanvas_);
    vmtFlyout.init(workspace);
    vmtFlyout.autoClose = true;
};

export const BlocklyComponent = forwardRef((props, ref) => {
    const { workspaceSetter } = props;
    const { initialXml, children, ...rest } = props;

    const { session: { theme } } = useAppContext();

    // const toolbox = useRef(null);
    // const blocklyDiv = React.useRef(null);
    const innerRef = useRef(null);
    const blocklyDiv = useCombinedRefs(ref, innerRef);

    const [workspace, setWorkspace] = useState(null);

    useEffect(() => {
        const wsOptions = {
            // toolbox: toolbox.current,
            ...rest,
        };
        wsOptions.plugins = {
            ...wsOptions.plugins,
            ...VmtTypesCheckPluginInfo,
        }

        const primaryWorkspace = Blockly.inject(
            blocklyDiv.current,
            wsOptions,
        );

        // set up a theme
        primaryWorkspace.setTheme(theme.themeName === Themes.Dark ? vmtDarkTheme : vmtLightTheme);

        registerVmtFlyout(primaryWorkspace);

        initialXml && (Blockly.Xml.domToWorkspace(Blockly.utils.xml.textToDom(initialXml), primaryWorkspace));//setXml(initialXml);
        workspaceSetter && workspaceSetter(primaryWorkspace);
        setWorkspace(primaryWorkspace);


        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialXml, workspaceSetter]);

    useEffect(() => {
        console.log('THEME', theme.themeName);
        // set up a theme
        workspace && workspace.setTheme(theme.themeName === Themes.Dark ? vmtDarkTheme : vmtLightTheme);
    }, [theme.themeName, workspace]);

    return (
        <>
            <div ref={blocklyDiv} id="blocklyDiv" />
            {/* <xml xmlns="https://developers.google.com/blockly/xml" is="blockly" style={{ display: 'none' }} ref={toolbox}>
                {children}
            </xml> */}
        </>
    );
});
